/**
 * Datapoint drill-down (contracts-plan.md §7.1: "both carry the sponsor and
 * date of every datapoint (the datapoint list is one click from the
 * value)"). Opened by clicking a rate-card row.
 */
import { useEffect, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { Modal, EmptyState } from '../../../ui';
import { getEntryDatapoints } from '../../api/rateCard';
import type { RcDatapoint } from '../../api/rateCard';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entryId: string;
  officialName: string;
  kind: string;
}

export function DatapointsModal({ isOpen, onClose, entryId, officialName, kind }: Props) {
  const [rows, setRows] = useState<RcDatapoint[] | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setRows(null);
    getEntryDatapoints(entryId)
      .then((result) => setRows(result.datapoints.filter((d) => d.kind === kind)))
      .catch(() => setRows([]));
  }, [isOpen, entryId, kind]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${officialName} — datapoints`} maxWidth="max-w-2xl">
      <div data-testid="rc-datapoints-modal">
        {rows === null && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

        {rows !== null && rows.length === 0 && (
          <EmptyState icon={FileSearch} title="No datapoints yet" hint="Nothing accepted for this line yet (§7.3)." />
        )}

        {rows !== null && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="py-1.5 pr-3">Value</th>
                  <th className="py-1.5 pr-3">Label</th>
                  <th className="py-1.5 pr-3">Sponsor</th>
                  <th className="py-1.5 pr-3">Date</th>
                  <th className="py-1.5 pr-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((dp) => {
                  // §11 Global (G7, G8): every number shows its label and its
                  // provenance on hover — this drill-down is the one place in
                  // the tab where the API already returns both on every row.
                  const locator = [
                    dp.provenance?.file,
                    dp.provenance?.sheet,
                    dp.provenance?.cell,
                    dp.provenance?.section,
                    dp.provenance?.page,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <tr key={dp.id} data-testid={`rc-datapoint-${dp.id}`}>
                      <td
                        className="py-1.5 pr-3 text-gray-900 dark:text-gray-100"
                        title={locator ? `${dp.label} — ${locator}` : dp.label}
                      >
                        {dp.value ?? dp.value_text}
                      </td>
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{dp.label}</td>
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{dp.sponsor_name ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{dp.agreed_date}</td>
                      <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{dp.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default DatapointsModal;
